import { useCallback, useState } from "react";
import PaymentMethodModal from "../../../components/common/PaymentMethodModal";
import {
  ConfirmationMethodProps,
  ConfirmationMethodResult,
  SelectedPaymentType,
  SnackBarStates,
} from "../../../components/common/PaymentMethodModal/types";
import { useTranslation } from "react-i18next";
import { executeWithdrawV2 } from "../../../setup/api/services/withdraws";
import {
  ExecuteWithdrawBody,
  IWithdrawDetailsResponseData,
  WithdrawalExecutorType,
} from "../../../setup/api/services/withdraws/models";

interface IWithdrawModal {
  open: boolean;
  onClose: () => void;
  paymentAmount: number;
  userId?: number;
  withdrawId?: string;
  withdrawDetails: IWithdrawDetailsResponseData;
}

const WithdrawModal = ({
  open,
  onClose,
  paymentAmount,
  userId,
  withdrawId,
  withdrawDetails,
}: IWithdrawModal) => {
  const { t } = useTranslation();

  const [amount, setAmount] = useState<number | string>();
  const [gatewayPaymentId, setGatewayPaymentId] = useState<string>();
  const [targetContractDomainId, setTargetContractDomainId] = useState<
    string | undefined
  >();

  const [transactionDate, setTransactionDate] = useState<Date | null>(
    new Date()
  );

  const handleSingleViewExecuteWithdraw = useCallback(
    async (id: string): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      let shouldClose = false;
      let success = false;

      try {
        const result = await executeWithdrawV2(id, {
          withdrawalExecutorType: WithdrawalExecutorType.SINGLE_VIEW,
        });
        const isSuccess = !!result?.data?.success;
        if (isSuccess) {
          snackBarStates = {
            title: result?.data?.msg || t("lbl_cash_out_completed"),
            color: "success",
          };
          shouldClose = true;
          success = true;
        } else {
          snackBarStates = { title: t("lbl_cash_out_failed"), color: "error" };
          success = false;
        }
      } catch (error: any) {
        snackBarStates = {
          title: JSON.stringify(error?.message),
          color: "error",
        };
        success = false;
      } finally {
        shouldClose = true;
        setTimeout(() => window.location.reload(), 3000);
      }
      return { snackBarStates, shouldClose, success };
    },
    [t]
  );

  const handleManualWithdraw = useCallback(
    async (id: string): Promise<ConfirmationMethodResult> => {
      let snackBarStates: SnackBarStates | undefined;
      let shouldClose = false;
      let success = false;

      const manualWithdrawBody: ExecuteWithdrawBody = {
        withdrawalExecutorType: WithdrawalExecutorType.MANUAL,
        gatewayPaymentId: String(gatewayPaymentId),
        transactionDate: transactionDate
          ? transactionDate?.toISOString()
          : new Date().toISOString(),
      };
      try {
        const result = await executeWithdrawV2(id, manualWithdrawBody);
        const isSuccess = !!result?.data?.success;
        if (isSuccess) {
          snackBarStates = {
            title: result?.data?.msg || t("lbl_cash_out_completed"),
            color: "success",
          };
        }
      } catch (error: any) {
        snackBarStates = {
          title: JSON.stringify(error?.message),
          color: "error",
        };
        success = false;
      } finally {
        shouldClose = true;
        setTimeout(() => window.location.reload(), 3000);
      }
      return { snackBarStates, shouldClose, success };
    },
    [gatewayPaymentId, t, transactionDate]
  );

  const onConfirm = useCallback(
    async ({
      selectionType,
    }: ConfirmationMethodProps): Promise<ConfirmationMethodResult> => {
      if (!withdrawId) {
        return { shouldClose: false, success: false };
      }

      if (selectionType === SelectedPaymentType.SINGLE_VIEW) {
        const result = await handleSingleViewExecuteWithdraw(withdrawId);
        return result;
      }

      if (selectionType === SelectedPaymentType.BANK) {
        const result = await handleManualWithdraw(withdrawId);
        return result;
      }

      return { shouldClose: false, success: false };
    },
    [handleManualWithdraw, handleSingleViewExecuteWithdraw, withdrawId]
  );

  return (
    <PaymentMethodModal
      open={open}
      onClose={onClose}
      amount={amount}
      setAmount={setAmount}
      gatewayPaymentId={gatewayPaymentId}
      setGatewayPaymentId={setGatewayPaymentId}
      targetContractDomainId={targetContractDomainId}
      setTargetContractDomainId={setTargetContractDomainId}
      onConfirm={onConfirm}
      paymentAmount={paymentAmount}
      userId={userId}
      transactionDate={transactionDate}
      setTransactionDate={setTransactionDate}
      bankDetails={withdrawDetails?.bank}
    />
  );
};

export default WithdrawModal;
